import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { FC, ReactNode, StrictMode } from 'react';
import { render } from 'react-dom';
import { BrowserRouter } from 'react-router-dom';
import { App } from 'src/app';
import { GlobalErrorHandler } from 'src/components/global-error-handler';
import { Notifications } from 'src/components/notification';
import { isNetworkError, isServerError } from 'src/hooks/use-axios';
import { AxiosInstanceProvider } from 'src/hooks/use-axios/axios-instance';
import { useCognito } from 'src/hooks/use-cognito';
import { NotificationsProvider } from 'src/hooks/use-notifications';
import { ReauthenticateProvider } from 'src/hooks/use-reauthenticate';
import { DeveloperToolsProvider } from 'src/pages/developer-tools/use-developer-tools';
import { createAxiosInstance } from 'src/utils/axios-instance';
import { getBreadcrumbsForSentry } from 'src/utils/breadcrumbs';
import { getSimplifiedUrl } from 'src/utils/report-error';

if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://d888e86e50654461b4ed7712f9757df2@sentry.io/1832012',
    release: process.env.CI_COMMIT_SHA,
    environment: PRISMA_CONFIG.stage,
    beforeSend(event, hint) {
      const error = hint?.originalException;

      // assumption: we get "ChunkLoadError"s from webpack, because of flaky connections
      // on the client as we can't fix those, we'll ignore them here
      if (error instanceof Error && error.name === 'ChunkLoadError')
        return null;

      // Group Errors With Greater Granularity
      // see https://docs.sentry.io/platforms/javascript/data-management/event-grouping/sdk-fingerprinting/#group-errors-with-greater-granularity
      if (isNetworkError(error)) {
        const { url, method } = error.config;
        const { path, service } = getSimplifiedUrl(url);
        event.fingerprint = [
          '{{ default }}',
          PRISMA_CONFIG.stage,
          service,
          path,
          String(method),
        ];
      }
      if (isServerError(error)) {
        const { url, method } = error.config;
        const { path, service } = getSimplifiedUrl(url);
        event.fingerprint = [
          '{{ default }}',
          PRISMA_CONFIG.stage,
          service,
          path,
          String(method),
          String(error.response.status),
        ];
      }
      if (error && typeof (error as any).code === 'string') {
        event.fingerprint = [
          '{{ default }}',
          PRISMA_CONFIG.stage,
          (error as any).code,
        ];
      }

      event.extra = { ...event.extra, breadcrumbs: getBreadcrumbsForSentry() };

      return event;
    },
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 0.1, // measure 10% of the traffic (to not exceed quota)
    normalizeDepth: 10,
    enabled: location.protocol === 'https:',
  });
}

const AxiosInstance: FC<{ children: ReactNode }> = ({ children }) => {
  const cognito = useCognito();
  return (
    <AxiosInstanceProvider value={createAxiosInstance(cognito)}>
      {children}
    </AxiosInstanceProvider>
  );
};

render(
  <StrictMode>
    <DeveloperToolsProvider>
      <AxiosInstance>
        <BrowserRouter basename={__webpack_public_path__}>
          {/*
          React handles errors differently between development and production mode.
          During development catched errors are still marked as uncaught.
          For that reason our global error handling would kick in even though we already
          handled that error - that's why we'll just deactivate the global error handler
          during development.
          See https://github.com/facebook/react/issues/10384#issuecomment-334142138
        */}
          {process.env.NODE_ENV === 'production' && <GlobalErrorHandler />}
          <NotificationsProvider>
            <Notifications />

            <ReauthenticateProvider>
              <App />
            </ReauthenticateProvider>
          </NotificationsProvider>
        </BrowserRouter>
      </AxiosInstance>
    </DeveloperToolsProvider>
  </StrictMode>,
  document.getElementById('app')
);
